import { Observable, combineLatest, of } from 'rxjs';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { ConfigcatApiService } from './configcat-api.service';
import { environment } from '~env/environment';
import { uuidv4 } from '~shared/utils/uuid.util';
import { isPlatformBrowser } from '@angular/common';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { User } from 'configcat-js-ssr';
import { FeatureToggleEnum, NUMBER_0, NUMBER_100 } from '~shared/constants';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

@Injectable({
  providedIn: 'root',
})
/**
 * @class ActiveFeaturesService
 * @package-private
 * @description This class will check the feature flag status
 * defined in config cat dashboard and return the boolean response
 */
export class ActiveFeaturesConfigcatService {
  private readonly overrideSuffix = '_override';
  private envFeatureConfig: { [key: string]: number };

  constructor(
    private configCatService: ConfigcatApiService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {
    this.envFeatureConfig = Object.entries(environment.featureConfig()).reduce(
      (acc, [key, value]) => ({ ...acc, [key.toLowerCase()]: value }),
      {}
    );
  }

  getFeatureValue(
    featureName: string,
    defaultValue?: any,
    user: User = new User(uuidv4(), undefined, getCountryDomainCode(this.webAppBuyerRequest))
  ): Observable<string> {
    try {
      const configCatClient = this.configCatService.createInstance();
      return new Observable<string>((observer) =>
        configCatClient
          .getValueAsync(featureName, defaultValue, user)
          .then((featureFlagValue) => {
            if (featureFlagValue !== undefined) {
              observer.next(featureFlagValue);
              observer.complete();
              return;
            }

            observer.next(defaultValue);
            observer.complete();
          })
          .catch((err) => {
            console.error(err);
            observer.next(defaultValue);
            observer.complete();
          })
      );
    } catch (ex) {
      return of(defaultValue);
    }
  }

  isFeatureEnabled(
    featureName: string,
    defaultValue?: any,
    user: User = new User(uuidv4(), undefined, getCountryDomainCode(this.webAppBuyerRequest))
  ): Observable<boolean> {
    const envFFName = featureName.toLowerCase();
    const overriddenFFName = `${envFFName}${this.overrideSuffix}`;
    const overriddenFFValue = this.envFeatureConfig[overriddenFFName];

    if (overriddenFFValue !== undefined) {
      return of(Boolean(overriddenFFValue));
    }

    try {
      const configCatClient = this.configCatService.createInstance();
      return new Observable<boolean>((observer) =>
        configCatClient
          .getValueAsync(featureName, defaultValue, user)
          .then((featureFlagValue) => {
            if (featureFlagValue !== undefined) {
              observer.next(featureFlagValue);
              observer.complete();
              return;
            }

            const envFFValue = this.envFeatureConfig[envFFName];
            if (envFFValue !== undefined) {
              observer.next(Boolean(envFFValue));
              observer.complete();
              return;
            }

            observer.next(defaultValue);
            observer.complete();
          })
          .catch((err) => {
            console.error(err);
            observer.next(false);
            observer.complete();
          })
      );
    } catch (ex) {
      return of(false);
    }
  }

  isFeatureEnabledAndReady(featureName: string, defaultValue?: any, user?: User): Observable<boolean> {
    return this.configCatService.ready$.pipe(
      filter((ready) => ready),
      mergeMap(() => this.isFeatureEnabled(featureName, defaultValue, user))
    );
  }

  isFeatureEnabledAndReadyV2(featureName: string, defaultValue?: any, user?: User): Observable<boolean> {
    if (isPlatformBrowser(this.platformId)) {
      return this.configCatService.ready$.pipe(
        filter((ready) => ready),
        mergeMap(() => this.isFeatureEnabled(featureName, defaultValue, user))
      );
    }
    return this.isFeatureEnabled(featureName, defaultValue, user);
  }

  getFeatureValueWhenReady(featureName: string, defaultValue?: any, user?: User): Observable<string> {
    if (isPlatformBrowser(this.platformId)) {
      return this.configCatService.ready$.pipe(
        filter((ready) => ready),
        mergeMap(() => this.getFeatureValue(featureName, defaultValue, user))
      );
    }
    return this.getFeatureValue(featureName, defaultValue, user);
  }

  isFeatureEnabledForCurrentCountry$(
    featureName: string,
    userId = uuidv4(),
    email = '',
    country = getCountryDomainCode(this.webAppBuyerRequest)
  ): Observable<boolean> {
    return this.isFeatureEnabled(featureName, false, new User(userId, email, country));
  }
  isFeatureEnabledReadyForCurrentCountry$(
    featureName: string,
    userId = uuidv4(),
    email = '',
    country = getCountryDomainCode(this.webAppBuyerRequest)
  ): Observable<boolean> {
    if (isPlatformBrowser(this.platformId)) {
      return this.configCatService.ready$.pipe(
        filter((ready) => ready),
        mergeMap(() => this.isFeatureEnabled(featureName, false, new User(userId, email, country)))
      );
    }
    return this.isFeatureEnabled(featureName, false, new User(userId, email, country));
  }

  isFeatureEnabledBasedOnPercentage(featureName: string, defaultValue?: any, userId = uuidv4(), email = '') {
    return this.getFeatureValueWhenReady(
      featureName,
      defaultValue,
      new User(userId, email, getCountryDomainCode(this.webAppBuyerRequest))
    ).pipe(map(this.isHit));
  }

  isHit = (percentage): boolean => {
    const isNumber = typeof percentage === 'number';

    if (!isNumber || percentage < NUMBER_0 || percentage > NUMBER_100) {
      return false;
    }

    return Math.random() * NUMBER_100 < percentage;
  };

  aggregateFeatureFlagsMap(
    featureToggleEnums: (FeatureToggleEnum | string)[]
  ): Observable<Partial<Record<FeatureToggleEnum | string, boolean>>> {
    return combineLatest(featureToggleEnums.map((f) => this.isFeatureEnabled(f))).pipe(
      take(1),
      map((flags) =>
        flags.reduce((featureFlagMap, bool, idx) => ({ ...featureFlagMap, [featureToggleEnums[idx]]: bool }), {})
      )
    );
  }

  isStoryblokCustomPLPEnabled(): Observable<boolean> {
    return this.isFeatureEnabled(FeatureToggleEnum.FF_MACO_STORYBLOK_PLPS, false, {
      identifier: 'custom',
    });
  }
}
